"use client";
import { cn } from "@/lib/utils";
import { Slot } from "@radix-ui/react-slot";
import { cva, VariantProps } from "class-variance-authority";
import { useFormikContext } from "formik";
import { Loader2Icon } from "lucide-react";
import React from "react";

// type Props = {
//   color?:
//     | "primary"
//     | "secondary"
//     | "white"
//     | "blue"
//     | "dark"
//     | "green"
//     | "amber"
//     | "";
//   size?: "base" | "sm";
//   outline?: boolean;
//   active?: boolean;
//   as?: ReactNode | string;
//   href?: string;
//   target?: string;
//   roundFull?: boolean;
//   rel?: string;
// } & ComponentPropsWithRef<"button">;

export const buttonVariants = cva(
  [
    "inline-flex justify-center items-center gap-2 disabled:opacity-50",
    "rounded-md focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring [&_svg]:size-4",
    "whitespace-nowrap transition-colors disabled:pointer-events-none",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0",
    "cursor-pointer",
    "border",
  ],
  {
    variants: {
      variant: {
        primary: "bg-primary text-primary-foreground border-primary hover:bg-primary/90", // prettier-ignore
        secondary: "bg-secondary border-secondary text-secondary-foreground hover:bg-secondary/80", // prettier-ignore
        blue: "bg-blue-600 border-blue-600 text-white hover:bg-blue-600/80 dark:bg-blue-900 dark:border-blue-600", // prettier-ignore
        dark: "bg-dark text-dark-foreground hover:bg-dark/80",
        green: "bg-green-700 text-white hover:bg-green-700/80 border border-green-600", // prettier-ignore
        amber: "bg-amber text-amber-foreground hover:bg-amber/80",
        default: "bg-primary text-primary-foreground hover:bg-primary/90 border-primary", // prettier-ignore
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 border-destructive", // prettier-ignore
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground", // prettier-ignore
        ghost: "border-transparent hover:bg-accent hover:text-accent-foreground", // prettier-ignore
        link: "text-primary underline-offset-4 hover:underline",
        input: "border border-input bg-white dark:bg-black",
      },
      size: {
        default: "px-3 py-2 h-10", // "h-9 px-4 py-2",
        sm: "px-2 py-1", // "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "size-9",
      },
      roundFull: {
        true: "rounded-full",
        false: "rounded-md",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      roundFull: false,
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, roundFull, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className, roundFull }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export const FormikSubmit = ({ children, ...props }: ButtonProps) => {
  const { isSubmitting, handleSubmit } = useFormikContext();
  return (
    <Button
      {...props}
      type="submit"
      disabled={isSubmitting || props.disabled}
      //@ts-ignore
      onClick={handleSubmit}
    >
      <>
        {isSubmitting && <Loader2Icon className="size-4 animate-spin" />}
        {children}
      </>
    </Button>
  );
};

export default Button;
